import React, {createContext, useContext, useEffect, useState, useMemo, ReactNode} from 'react';
import * as signalR from '@microsoft/signalr';
import {STORAGE_KEY} from '@/utils/enums';
import {adminStorage, orgStorage, userStorage} from '@/utils/services/storage.service';

interface SignalRContextType {
	connection: signalR.HubConnection | null;
	isConnected: boolean;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

let hubConnection: signalR.HubConnection | null = null;
const apiBaseURL = process.env.REACT_APP_API_URL || '';
const notificationHubURL = `${apiBaseURL.replace(/\/api\/?$/, '').replace(/\/$/, '')}/hubs/notification`;

const getAuthToken = (): string => {
	const {pathname} = window.location;

	if (pathname.startsWith('/organization')) {
		return orgStorage.get<string>(STORAGE_KEY.JWT_BEARER_TOKEN) || '';
	}

	if (pathname.startsWith('/admin')) {
		return adminStorage.get<string>(STORAGE_KEY.JWT_BEARER_TOKEN) || '';
	}

	return userStorage.get<string>(STORAGE_KEY.JWT_BEARER_TOKEN) || '';
};

const getConnection = (): signalR.HubConnection => {
	if (!hubConnection) {
		hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(notificationHubURL, {
				accessTokenFactory: getAuthToken,
				transport: signalR.HttpTransportType.WebSockets,
			})
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Trace)
			.build();
	}
	return hubConnection;
};

export const resetConnection = async () => {
	if (hubConnection) {
		if (hubConnection.state !== signalR.HubConnectionState.Disconnected) {
			await hubConnection.stop();
		}
		hubConnection = null;
		console.log('SignalR connection reset');
	}
};

export const SignalRProvider: React.FC<{children: ReactNode}> = ({children}) => {
	const [isConnected, setIsConnected] = useState<boolean>(false);
	const connection = getConnection();

	useEffect(() => {
		connection.onreconnecting(() => setIsConnected(false));
		connection.onreconnected(() => setIsConnected(true));
		connection.onclose(() => setIsConnected(false));
	}, [connection]);

	const contextValue = useMemo(() => ({connection, isConnected}), [isConnected]);

	return <SignalRContext.Provider value={contextValue}>{children}</SignalRContext.Provider>;
};

export const useSignalR = () => {
	const context = useContext(SignalRContext);
	if (!context) {
		throw new Error('useSignalR must be used within a SignalRProvider');
	}
	return context;
};
